import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Search, FileText, Receipt, UserPlus, Package,
  Users, Wallet, Coins, BarChart3, Settings2,
  ArrowLeft, Plus, Trash2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface ComandoSistema {
  id: string;
  nombre: string;
  icono: LucideIcon;
  categoria: 'acciones' | 'navegacion';
  atajo: string;
  accion: () => void;
}

interface ComandoPersonalizado {
  id: string;
  nombre: string;
  categoria: 'acciones' | 'navegacion';
  atajo: string;
  accionId: string;
}

type Vista = 'principal' | 'administrar' | 'nuevo';

// ─── Constantes ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'senciyo:comandos-personalizados';

const ATAJOS_RESERVADOS = [
  'Ctrl+A', 'Ctrl+C', 'Ctrl+V', 'Ctrl+X', 'Ctrl+Z', 'Ctrl+S', 'Ctrl+O',
  'Ctrl+N', 'Ctrl+R', 'Ctrl+H', 'Ctrl+T', 'Ctrl+W', 'F5', 'F11', 'F12',
  'Alt+F4', 'Alt+Tab',
  // App — acciones
  'Ctrl+K', 'Ctrl+Shift+F', 'Ctrl+Shift+B', 'Ctrl+Shift+C', 'Ctrl+Shift+P',
  // App — navegación
  'Ctrl+1', 'Ctrl+2', 'Ctrl+3', 'Ctrl+4', 'Ctrl+5', 'Ctrl+6',
];

const ACCIONES_DISPONIBLES: { id: string; nombre: string; categoria: 'acciones' | 'navegacion' }[] = [
  { id: 'nueva-factura',    nombre: 'Nueva Factura',       categoria: 'acciones'   },
  { id: 'nueva-boleta',     nombre: 'Nueva Boleta',        categoria: 'acciones'   },
  { id: 'nuevo-cliente',    nombre: 'Nuevo Cliente',       categoria: 'acciones'   },
  { id: 'nuevo-producto',   nombre: 'Nuevo Producto',      categoria: 'acciones'   },
  { id: 'ir-comprobantes',  nombre: 'Ir a Comprobantes',   categoria: 'navegacion' },
  { id: 'ir-productos',     nombre: 'Ir a Productos',      categoria: 'navegacion' },
  { id: 'ir-clientes',      nombre: 'Ir a Clientes',       categoria: 'navegacion' },
  { id: 'ir-cajas',         nombre: 'Ir a Cajas',          categoria: 'navegacion' },
  { id: 'ir-cobranzas',     nombre: 'Ir a Cobranzas',      categoria: 'navegacion' },
  { id: 'ir-indicadores',   nombre: 'Ir a Indicadores',    categoria: 'navegacion' },
  { id: 'ir-configuracion', nombre: 'Ir a Configuración',  categoria: 'navegacion' },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface PaletaComandosProps {
  isOpen: boolean;
  onClose: () => void;
}

// ─── Componente ───────────────────────────────────────────────────────────────

export const PaletaComandos = ({ isOpen, onClose }: PaletaComandosProps) => {
  const navigate = useNavigate();

  // Comandos del sistema
  const comandosSistema: ComandoSistema[] = useMemo(() => [
    { id: 'nueva-factura',    nombre: 'Nueva Factura',          icono: FileText,  categoria: 'acciones',   atajo: 'Ctrl+Shift+F', accion: () => navigate('/comprobantes/emision?tipo=factura') },
    { id: 'nueva-boleta',     nombre: 'Nueva Boleta',           icono: Receipt,   categoria: 'acciones',   atajo: 'Ctrl+Shift+B', accion: () => navigate('/comprobantes/emision?tipo=boleta')  },
    { id: 'nuevo-cliente',    nombre: 'Nuevo Cliente',          icono: UserPlus,  categoria: 'acciones',   atajo: 'Ctrl+Shift+C', accion: () => navigate('/clientes?action=create')            },
    { id: 'nuevo-producto',   nombre: 'Nuevo Producto',         icono: Package,   categoria: 'acciones',   atajo: 'Ctrl+Shift+P', accion: () => navigate('/productos?action=create')           },
    { id: 'ir-comprobantes',  nombre: 'Comprobantes',           icono: FileText,  categoria: 'navegacion', atajo: 'Ctrl+1', accion: () => navigate('/comprobantes')                      },
    { id: 'ir-productos',     nombre: 'Productos',              icono: Package,   categoria: 'navegacion', atajo: 'Ctrl+2', accion: () => navigate('/productos')                         },
    { id: 'ir-clientes',      nombre: 'Clientes',               icono: Users,     categoria: 'navegacion', atajo: 'Ctrl+3', accion: () => navigate('/clientes')                          },
    { id: 'ir-cajas',         nombre: 'Cajas',                  icono: Wallet,    categoria: 'navegacion', atajo: 'Ctrl+4', accion: () => navigate('/cajas')                             },
    { id: 'ir-cobranzas',     nombre: 'Cobranzas',              icono: Coins,     categoria: 'navegacion', atajo: 'Ctrl+5', accion: () => navigate('/cobranzas')                         },
    { id: 'ir-indicadores',   nombre: 'Indicadores',            icono: BarChart3, categoria: 'navegacion', atajo: 'Ctrl+6', accion: () => navigate('/indicadores')                       },
    { id: 'ir-configuracion', nombre: 'Configuración',          icono: Settings2, categoria: 'navegacion', atajo: '',       accion: () => navigate('/configuracion')                     },
  ], [navigate]);

  const mapaAcciones = useMemo(() => {
    const m = new Map<string, () => void>();
    comandosSistema.forEach(c => m.set(c.id, c.accion));
    return m;
  }, [comandosSistema]);

  // Estado
  const [vista, setVista] = useState<Vista>('principal');
  const [busqueda, setBusqueda] = useState('');
  const [indiceActivo, setIndiceActivo] = useState(0);
  const [comandosPersonalizados, setComandosPersonalizados] = useState<ComandoPersonalizado[]>([]);
  const [nuevoCmd, setNuevoCmd] = useState({ nombre: '', atajo: '', categoria: 'acciones' as 'acciones' | 'navegacion', accionId: '' });
  const [conflicto, setConflicto] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Cargar personalizados de localStorage
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try { setComandosPersonalizados(JSON.parse(raw)); } catch { /* ignorar */ }
    }
  }, []);

  const guardarPersonalizados = (lista: ComandoPersonalizado[]) => {
    setComandosPersonalizados(lista);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lista));
  };

  // Todos los comandos combinados para la vista principal
  const todosLosCmds = useMemo(() => {
    const personalizadosComoSistema: ComandoSistema[] = comandosPersonalizados
      .filter(c => mapaAcciones.has(c.accionId))
      .map(c => {
        const accionBase = ACCIONES_DISPONIBLES.find(a => a.id === c.accionId);
        return {
          id: c.id,
          nombre: c.nombre,
          icono: Package,
          categoria: c.categoria,
          atajo: c.atajo,
          accion: mapaAcciones.get(c.accionId)!,
        } satisfies ComandoSistema;
        void accionBase;
      });
    return [...comandosSistema, ...personalizadosComoSistema];
  }, [comandosSistema, comandosPersonalizados, mapaAcciones]);

  const filtrados = useMemo(() => {
    const q = busqueda.toLowerCase().trim();
    if (!q) return todosLosCmds;
    return todosLosCmds.filter(c =>
      c.nombre.toLowerCase().includes(q) ||
      c.atajo.toLowerCase().includes(q)
    );
  }, [busqueda, todosLosCmds]);

  const acciones = useMemo(() => filtrados.filter(c => c.categoria === 'acciones'), [filtrados]);
  const navegacion = useMemo(() => filtrados.filter(c => c.categoria === 'navegacion'), [filtrados]);
  const ordenados = useMemo(() => [...acciones, ...navegacion], [acciones, navegacion]);

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setVista('principal');
      setBusqueda('');
      setIndiceActivo(0);
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => { setIndiceActivo(0); }, [busqueda]);

  useEffect(() => {
    itemRefs.current[indiceActivo]?.scrollIntoView({ block: 'nearest' });
  }, [indiceActivo]);

  const ejecutar = useCallback((cmd: ComandoSistema) => {
    cmd.accion();
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (vista !== 'principal') return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setIndiceActivo(prev => (prev + 1) % Math.max(ordenados.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setIndiceActivo(prev => (prev - 1 + Math.max(ordenados.length, 1)) % Math.max(ordenados.length, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = ordenados[indiceActivo];
      if (cmd) ejecutar(cmd);
    } else if (e.key === 'Escape') {
      onClose();
    }
  }, [vista, ordenados, indiceActivo, ejecutar, onClose]);

  // Validar conflicto de atajo
  const verificarConflicto = (atajo: string): string => {
    if (!atajo) return '';
    const norm = atajo.toLowerCase();
    if (ATAJOS_RESERVADOS.some(r => r.toLowerCase() === norm))
      return 'Este atajo ya está reservado por el sistema o el navegador';
    const enUso = todosLosCmds.find(c => c.atajo.toLowerCase() === norm);
    if (enUso) return `Ya usado por: ${enUso.nombre}`;
    return '';
  };

  const irANuevo = () => {
    setNuevoCmd({ nombre: '', atajo: '', categoria: 'acciones', accionId: '' });
    setConflicto('');
    setVista('nuevo');
  };

  const guardarNuevoComando = () => {
    const c = nuevoCmd;
    if (!c.nombre.trim() || !c.atajo.trim() || !c.accionId) return;
    if (conflicto) return;
    const nuevo: ComandoPersonalizado = {
      id: Date.now().toString(),
      nombre: c.nombre.trim(),
      atajo: c.atajo.trim(),
      categoria: c.categoria,
      accionId: c.accionId,
    };
    guardarPersonalizados([...comandosPersonalizados, nuevo]);
    setVista('administrar');
  };

  const eliminarPersonalizado = (id: string) => {
    guardarPersonalizados(comandosPersonalizados.filter(c => c.id !== id));
  };

  if (!isOpen) return null;

  // ─── Item de la lista ──────────────────────────────────────────────────────
  const renderItem = (cmd: ComandoSistema, globalIndex: number) => {
    const Icono = cmd.icono;
    const activo = globalIndex === indiceActivo;
    return (
      <button
        id={`paleta-comando-item-${cmd.id}`}
        name={`paletaComandoItem${cmd.id}`}
        key={cmd.id}
        type="button"
        ref={el => { itemRefs.current[globalIndex] = el; }}
        className={[
          'w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors',
          activo ? 'bg-surface-hover ring-1 ring-brand/20' : 'hover:bg-surface-hover',
        ].join(' ')}
        onMouseEnter={() => setIndiceActivo(globalIndex)}
        onClick={() => ejecutar(cmd)}
      >
        <div className="flex items-center gap-3">
          <Icono size={16} className="text-tertiary shrink-0" />
          <span className="text-ui-sm text-primary">{cmd.nombre}</span>
        </div>
        {cmd.atajo && (
          <span className="text-[10px] text-tertiary font-mono bg-surface-2 px-2 py-0.5 rounded">
            {cmd.atajo}
          </span>
        )}
      </button>
    );
  };

  // ─── Input reutilizable ────────────────────────────────────────────────────
  const inputClass = 'w-full px-3 py-2 rounded-lg border border-default bg-surface-0 text-ui-sm text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand/30';

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center pt-[12vh] bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl bg-surface-0 rounded-xl shadow-2xl border border-default overflow-hidden flex flex-col"
        style={{ maxHeight: '80vh' }}
        onClick={e => e.stopPropagation()}
      >

        {/* ══════════════════ VISTA PRINCIPAL ══════════════════ */}
        {vista === 'principal' && (
          <>
            <div className="px-4 pt-4 pb-3 border-b border-default">
              <div className="flex items-center justify-between mb-2">
                <p className="text-caption-medium text-tertiary">Comando Rápido</p>
                <button
                  id="paleta-comandos-administrar"
                  name="paletaComandosAdministrar"
                  type="button"
                  onClick={() => setVista('administrar')}
                  className="flex items-center gap-1.5 text-[11px] text-tertiary hover:text-brand transition-colors px-2 py-1 rounded-lg hover:bg-surface-hover"
                >
                  <Settings2 size={12} />
                  Administrar comandos
                </button>
              </div>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-tertiary" />
                <input
                  id="paleta-comandos-buscar"
                  name="paletaComandosBuscar"
                  ref={inputRef}
                  type="text"
                  value={busqueda}
                  onChange={e => setBusqueda(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Buscar o ejecutar comando..."
                  className="w-full pl-9 pr-4 py-2.5 bg-transparent text-ui-sm text-primary placeholder:text-tertiary focus:outline-none"
                />
              </div>
            </div>

            <div className="overflow-y-auto flex-1">
              {ordenados.length === 0 ? (
                <div className="py-10 text-center text-body-sm text-tertiary">Sin resultados</div>
              ) : (
                <>
                  {acciones.length > 0 && (
                    <div className="p-3">
                      <p className="text-[11px] font-semibold text-tertiary uppercase px-2 py-1.5 tracking-wide">Acciones</p>
                      {acciones.map((cmd, i) => renderItem(cmd, i))}
                    </div>
                  )}
                  {navegacion.length > 0 && (
                    <div className="p-3 border-t border-muted">
                      <p className="text-[11px] font-semibold text-tertiary uppercase px-2 py-1.5 tracking-wide">Ir a</p>
                      {navegacion.map((cmd, i) => renderItem(cmd, acciones.length + i))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="px-4 py-2.5 border-t border-muted bg-surface-1 flex items-center justify-center gap-4 text-[10px] text-tertiary">
              <span>↑↓ Navegar</span>
              <span>↵ Seleccionar</span>
              <span>Esc Cerrar</span>
            </div>
          </>
        )}

        {/* ══════════════════ VISTA ADMINISTRAR ══════════════════ */}
        {vista === 'administrar' && (
          <>
            <div className="px-4 py-3 border-b border-default flex items-center gap-3">
              <button
                id="paleta-comandos-admin-volver"
                name="paletaComandosAdminVolver"
                type="button"
                onClick={() => setVista('principal')}
                aria-label="Volver a comandos"
                className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-surface-hover transition-colors"
              >
                <ArrowLeft size={16} className="text-secondary" />
              </button>
              <p className="text-ui-sm font-semibold text-primary">Administrar comandos</p>
            </div>

            <div className="overflow-y-auto flex-1 p-4 space-y-6">
              {/* Personalizados */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-ui-sm font-semibold text-primary">Comandos personalizados</p>
                  <button
                    id="paleta-comandos-nuevo"
                    name="paletaComandosNuevo"
                    type="button"
                    onClick={irANuevo}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-brand bg-info-bg rounded-lg hover:bg-brand/10 transition-colors"
                  >
                    <Plus size={12} />
                    Nuevo comando
                  </button>
                </div>
                {comandosPersonalizados.length === 0 ? (
                  <div className="py-8 text-center text-body-sm text-tertiary rounded-lg border border-dashed border-default">
                    <p>No hay comandos personalizados</p>
                    <p className="text-caption mt-1">Crea uno para empezar</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {comandosPersonalizados.map(cmd => (
                      <div key={cmd.id} className="flex items-center justify-between p-3 bg-surface-1 rounded-lg border border-default">
                        <div>
                          <p className="text-ui-sm font-medium text-primary">{cmd.nombre}</p>
                          <p className="text-caption text-tertiary">{cmd.atajo}</p>
                        </div>
                        <button
                          id={`paleta-comandos-eliminar-${cmd.id}`}
                          name={`paletaComandosEliminar${cmd.id}`}
                          type="button"
                          onClick={() => eliminarPersonalizado(cmd.id)}
                          aria-label={`Eliminar comando ${cmd.nombre}`}
                          className="p-1.5 text-tertiary hover:text-error transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sistema (solo lectura) */}
              <div>
                <p className="text-ui-sm font-semibold text-primary mb-3">Comandos del sistema</p>
                <div className="space-y-2">
                  {comandosSistema.filter(c => c.atajo).map(cmd => {
                    const Icono = cmd.icono;
                    return (
                      <div key={cmd.id} className="flex items-center justify-between p-3 bg-surface-1 rounded-lg border border-default opacity-70">
                        <div className="flex items-center gap-3">
                          <Icono size={15} className="text-tertiary shrink-0" />
                          <div>
                            <p className="text-ui-sm font-medium text-primary">{cmd.nombre}</p>
                            <p className="text-caption text-tertiary">{cmd.atajo}</p>
                          </div>
                        </div>
                        <span className="text-[10px] text-tertiary">Sistema</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="px-4 py-2.5 border-t border-muted bg-surface-1 flex items-center justify-between">
              <p className="text-[10px] text-tertiary">Los comandos personalizados se guardan localmente</p>
              <button
                id="paleta-comandos-admin-cerrar"
                name="paletaComandosAdminCerrar"
                type="button"
                onClick={() => setVista('principal')}
                className="text-[10px] text-tertiary hover:text-brand transition-colors px-2 py-1 rounded"
              >
                Volver
              </button>
            </div>
          </>
        )}

        {/* ══════════════════ VISTA NUEVO COMANDO ══════════════════ */}
        {vista === 'nuevo' && (
          <>
            <div className="px-4 py-3 border-b border-default flex items-center gap-3">
              <button
                id="paleta-comandos-nuevo-volver"
                name="paletaComandosNuevoVolver"
                type="button"
                onClick={() => setVista('administrar')}
                aria-label="Volver a administrar comandos"
                className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-surface-hover transition-colors"
              >
                <ArrowLeft size={16} className="text-secondary" />
              </button>
              <p className="text-ui-sm font-semibold text-primary">Nuevo comando</p>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4">
              {/* Aviso atajos reservados */}
              <div className="p-3 bg-info-bg border border-info-border rounded-lg">
                <p className="text-caption-medium text-brand mb-1">Atajos reservados</p>
                <p className="text-caption text-secondary">
                  Ctrl+Shift+F/B/U/P · Ctrl+1–6 · Ctrl+K están en uso por el sistema.
                  Atajos del navegador (Ctrl+S, Ctrl+R, Ctrl+F, etc.) también están reservados.
                </p>
              </div>

              {/* Nombre */}
              <div>
                <label htmlFor="paleta-cmd-nombre" className="block text-caption-medium text-secondary mb-1.5">Nombre del comando</label>
                <input
                  id="paleta-cmd-nombre"
                  name="paletaCmdNombre"
                  type="text"
                  value={nuevoCmd.nombre}
                  onChange={e => setNuevoCmd(p => ({ ...p, nombre: e.target.value }))}
                  placeholder="Ej: Exportar productos"
                  className={inputClass}
                />
              </div>

              {/* Atajo */}
              <div>
                <label htmlFor="paleta-cmd-atajo" className="block text-caption-medium text-secondary mb-1.5">Atajo de teclado</label>
                <input
                  id="paleta-cmd-atajo"
                  name="paletaCmdAtajo"
                  type="text"
                  value={nuevoCmd.atajo}
                  onChange={e => {
                    const val = e.target.value;
                    setNuevoCmd(p => ({ ...p, atajo: val }));
                    setConflicto(verificarConflicto(val));
                  }}
                  placeholder="Ej: Ctrl+E, Alt+X, F9"
                  className={inputClass}
                />
                {conflicto && (
                  <p className="mt-1 text-caption text-error">⚠ {conflicto}</p>
                )}
              </div>

              {/* Acción */}
              <div>
                <label htmlFor="paleta-cmd-accion" className="block text-caption-medium text-secondary mb-1.5">Acción</label>
                <select
                  id="paleta-cmd-accion"
                  name="paletaCmdAccion"
                  value={nuevoCmd.accionId}
                  onChange={e => setNuevoCmd(p => ({ ...p, accionId: e.target.value }))}
                  className={inputClass}
                >
                  <option value="">Selecciona una acción...</option>
                  <optgroup label="Acciones">
                    {ACCIONES_DISPONIBLES.filter(a => a.categoria === 'acciones').map(a => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Navegación">
                    {ACCIONES_DISPONIBLES.filter(a => a.categoria === 'navegacion').map(a => (
                      <option key={a.id} value={a.id}>{a.nombre}</option>
                    ))}
                  </optgroup>
                </select>
              </div>

              {/* Categoría */}
              <div>
                <label htmlFor="paleta-cmd-categoria" className="block text-caption-medium text-secondary mb-1.5">Categoría</label>
                <select
                  id="paleta-cmd-categoria"
                  name="paletaCmdCategoria"
                  value={nuevoCmd.categoria}
                  onChange={e => setNuevoCmd(p => ({ ...p, categoria: e.target.value as 'acciones' | 'navegacion' }))}
                  className={inputClass}
                >
                  <option value="acciones">Acciones</option>
                  <option value="navegacion">Ir a</option>
                </select>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-default bg-surface-1 flex items-center justify-between gap-3">
              <button
                id="paleta-comandos-nuevo-cancelar"
                name="paletaComandosNuevoCancelar"
                type="button"
                onClick={() => setVista('administrar')}
                className="text-ui-sm text-secondary hover:text-primary transition-colors px-3 py-2 rounded-lg hover:bg-surface-hover"
              >
                Cancelar
              </button>
              <button
                id="paleta-comandos-nuevo-crear"
                name="paletaComandosNuevoCrear"
                type="button"
                onClick={guardarNuevoComando}
                disabled={!nuevoCmd.nombre.trim() || !nuevoCmd.atajo.trim() || !nuevoCmd.accionId || !!conflicto}
                className="px-5 py-2 text-ui-sm font-medium bg-brand text-white rounded-lg hover:bg-brand/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Crear comando
              </button>
            </div>
          </>
        )}

      </div>
    </div>,
    document.body
  );
};
